import { CertnAsset, SpringverifyAsset, ZincAsset } from '@kula-ai/assets';
import type { ProviderType } from '@/components/ats/actions/background-check/backgroundCheckTypes';
import { esignActivityErrorCode } from '@/components/shared/esignature/activity/esignActivityErrors';
import { withUniqueSvgIds } from './withUniqueSvgIds';

// Each copy gets its own <defs> ids — see `withUniqueSvgIds`. Zinc's gradient
// and SpringVerify's clip path otherwise break behind a hidden earlier copy.
const CertnLogo = withUniqueSvgIds(CertnAsset);
const ZincLogo = withUniqueSvgIds(ZincAsset);
const SpringverifyLogo = withUniqueSvgIds(SpringverifyAsset);

/**
 * Background checks as an interview-plan stage activity (ENG-4228, BE #6213).
 *
 * One activity kind per provider. The kind is what the stage-action endpoints
 * store; the provider is what the existing packages and order endpoints are
 * keyed on — this map is the only place that knows the two apart.
 *
 * Checkr is deliberately absent: the backend does not support it as an activity.
 *
 * `Logo` is the provider's square mark. It is drawn in a 20–24px box (picker
 * card, drawer header, plan card), where the `*WordmarkAsset` variants — a
 * 72x24 and an 830x250 lettering — shrink to an unreadable sliver. (DocuSign's
 * "wordmark" is the exception: that asset is its bare glyph.)
 */
export const BACKGROUND_CHECK_ACTIVITY = {
  certn_background_check: {
    provider: 'certn',
    name: 'Certn',
    Logo: CertnLogo,
  },
  zinc_background_check: {
    provider: 'zinc',
    name: 'Zinc',
    Logo: ZincLogo,
  },
  springverify_background_check: {
    provider: 'springverify',
    name: 'SpringVerify',
    Logo: SpringverifyLogo,
  },
} as const satisfies Record<
  string,
  { provider: ProviderType; name: string; Logo: unknown }
>;

export type BackgroundCheckActivityKind =
  keyof typeof BACKGROUND_CHECK_ACTIVITY;

export const BACKGROUND_CHECK_ACTIVITY_KINDS = Object.keys(
  BACKGROUND_CHECK_ACTIVITY
) as BackgroundCheckActivityKind[];

export const isBackgroundCheckActivityKind = (
  kind?: string | null
): kind is BackgroundCheckActivityKind =>
  BACKGROUND_CHECK_ACTIVITY_KINDS.includes(kind as BackgroundCheckActivityKind);

/** Which kinds may be offered right now — the flag plus a connected provider. */
export type BackgroundCheckActivityGates = Record<
  BackgroundCheckActivityKind,
  boolean
>;

export const getBackgroundCheckActivityTitle = (
  kind: BackgroundCheckActivityKind
) => `${BACKGROUND_CHECK_ACTIVITY[kind].name} background check`;

export const getBackgroundCheckActivityDescription = (
  kind: BackgroundCheckActivityKind
) =>
  `Order a ${BACKGROUND_CHECK_ACTIVITY[kind].name} background check for candidates in this stage`;

export const BACKGROUND_CHECK_ACTIVITY_COPY = {
  PACKAGE_LABEL: 'Package',
  PACKAGE_PLACEHOLDER: 'Select a package',
  PACKAGE_REQUIRED: 'Choose a package.',
  PACKAGES_ERROR: 'Could not load the packages for this provider.',
  NO_PACKAGES: 'No packages found for this provider',
  CANCEL: 'Cancel',
  ADD: 'Add background check',
  SAVE: 'Save background check',
  CREATE_SUCCESS: 'Activity created successfully',
  UPDATE_SUCCESS: 'Activity updated successfully',
  SAVE_ERROR: 'Could not save the background check. Try again.',
} as const;

// Suggested copy from the backend handoff doc; PM copy is still an open question.
export const BACKGROUND_CHECK_ACTIVITY_ERROR_COPY: Record<string, string> = {
  err_background_check_activity_not_enabled:
    "Background checks in the interview plan aren't enabled for this account.",
  err_background_check_integration_not_connected:
    'Connect this provider in Settings before adding the check.',
  err_background_check_package_required: 'Choose a package.',
  err_background_check_package_invalid:
    "This package can't be used. Pick it again from the list.",
};

/**
 * Toast copy for a rejected activity save. The stage-action endpoints reject
 * with `err_*` codes in the same shapes the e-sign activity handles, so the code
 * is read with its parser; anything unrecognised gets generic copy rather than
 * leaking a raw code or an axios message.
 */
export const backgroundCheckActivityErrorCopy = (error?: unknown): string => {
  const code = esignActivityErrorCode(error);
  return (
    (code && BACKGROUND_CHECK_ACTIVITY_ERROR_COPY[code]) ??
    BACKGROUND_CHECK_ACTIVITY_COPY.SAVE_ERROR
  );
};
